import { DeckOverlayPlugin, FlightLayerFacade } from '@fe/flight-layer';
import { FeMap } from '@fe/maplibre-extensions';
import { Subscription } from 'maplibre-gl';
import { ParticleColor, WeatherSettings, WeatherSettingsChangeSource } from '../../settings/settings';
import { MessageType } from '../../worker/actor/actor-messages';
import { Dispatcher } from '../../worker/dispatcher';
import { WeatherLayerBase } from '../weather-layer-base';

export class WindParticlesLayer extends WeatherLayerBase {
    private themeSubscription: Subscription | null = null;
    private flightLayerFacade: FlightLayerFacade | null = null;

    constructor(
        map: FeMap,
        workspaceId: number,
        dispatcher: Dispatcher,
        productId: number,
        weatherTopSlot: string,
        settings: WeatherSettings,
        flightLayerFacade: FlightLayerFacade | null,
        subLayerName?: string
    ) {
        super(map, workspaceId, dispatcher, productId, weatherTopSlot, settings, subLayerName);
        this.onThemeChanged = this.onThemeChanged.bind(this);
        this.flightLayerFacade = flightLayerFacade;
    }

    private get deckPlugin(): DeckOverlayPlugin | null {
        return this.flightLayerFacade?.deckPlugin ?? null;
    }

    protected async loadFrame(frameId: number) {
        // Need to check this here, because there's only one global deck overlay so different weather layers could override themselves
        if (!this.shouldDisplay() || !this.deckPlugin?.props) {
            return;
        }
        // Disable particles first to avoid animation glitching
        const deckProps = this.deckPlugin?.props;
        deckProps.windParticles.layerName = this.baseId;
        deckProps.windParticles.data = null;
        deckProps.windParticles.isEnabled = false;
        this.deckPlugin.updateLayers();

        const response = await this.dispatcher.dispatch({
            type: MessageType.getWindComposite,
            data: {
                id: this.productId,
                frame: frameId,
                workspaceId: this.workspaceId
            }
        });

        if (!response || !this.attached) {
            return;
        }

        deckProps.windParticles.data = {
            data: response.data,
            width: response.width,
            height: response.height
        };
        deckProps.windParticles.isEnabled = true;
        this.deckPlugin.updateLayers();
    }

    protected onSettingsUpdated(source: WeatherSettingsChangeSource): void {
        if (source === WeatherSettingsChangeSource.winds) {
            this.setColor();
        }
    }

    protected onAdd(): void {
        this.setColor();
        this.themeSubscription = this.map.onThemeChanged(this.onThemeChanged);
    }

    protected onRemove(): void {
        this.themeSubscription?.unsubscribe();
        this.disableParticleDeckLayer();
    }

    protected onDisplayChanged(): void {
        if (this.shouldDisplay()) {
            this.loadFrame(this.visibleFrame!);
        } else {
            const deckWeatherProps = this.deckPlugin?.props;
            if (deckWeatherProps && deckWeatherProps.windParticles.layerName === this.baseId) {
                this.disableParticleDeckLayer();
            }
        }
    }

    private disableParticleDeckLayer(): void {
        const deckProps = this.deckPlugin?.props;
        if (!deckProps) {
            return;
        }

        deckProps.windParticles.isEnabled = false;
        deckProps.windParticles.data = null;
        deckProps.windParticles.layerName = null;
        this.deckPlugin.updateLayers();
    }

    private setColor(): void {
        const { windParticles } = this.deckPlugin?.props || {};
        if (!windParticles) {
            return;
        }

        const { winds } = this.settings;
        if (winds.particleColor === ParticleColor.auto) {
            windParticles.currentColor = (this.map as any).isLightStyle ? windParticles.colorDark : windParticles.colorLight;
        } else {
            windParticles.currentColor = winds.particleColor === ParticleColor.dark ? windParticles.colorDark : windParticles.colorLight;
        }
        windParticles.opacity = winds.particleOpacity;
        this.deckPlugin?.updateLayers();
    }

    private onThemeChanged(): void {
        this.setColor();
    }
}
